import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Modal,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import * as yup from 'yup';
import { greenColor } from '../../config/colors';
import { useCookiesLogin } from '../../config/cookies';
import { HOST_API } from '../../config/hosts';
import { tokens } from '../../theme';

import Header from '../../components/Header';
import HandlerSerasaSocoreCPF from '../../components/SerasaScoreCPF';
import HandlerSerasaSocorePj from '../../components/SerasaScorePj';
import { handleSerasaScore } from '../redsimulation/createConsulting';
import { useTranslation } from '../../hooks/useTranslation';
import { validateCNPJ, validateCPF } from '../../utils';
import ModalPendingConsult from '../../components/ModalPendingConsult';

const isCPF = (value) => value && value.length === 11;
const isCNPJ = (value) => value && value.length > 11;

export const formatCNPJ = (value) => {
  const onlyDigits = (value || '').replace(/\D/g, '');
  if (onlyDigits.length > 11) {
    return onlyDigits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*/, '$1.$2.$3/$4-$5');
  } else {
    return onlyDigits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2}).*/, '$1.$2.$3-$4');
  }
};

const Consulta = ({ handleRefresh, username }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { cookies } = useCookiesLogin();
  const [quantityCoins, setQuantityCoins] = useState(0);
  const { last_company: bussines_id } = cookies?.AUTH_TOKEN || {};
  const [loadingCoins, setLoadingCoins] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    const { token } = cookies.AUTH_TOKEN;
    fetch(`${HOST_API}/v1/dashboard/balance`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json().then((data) => ({ status: res.status, data })))
      .then(({ status, data }) => {
        setQuantityCoins(status === 200 ? data.amount : 0);
      })
      .finally(() => {
        setLoadingCoins(false);
      });
  }, []);

  return (
    <Box>
      {loadingCoins ? (
        <Typography variant="h5" color={colors.grey[100]} fontWeight="bold">
          {t('dashboard.remainingConsults')}
        </Typography>
      ) : quantityCoins !== 0 ? (
        handleRefresh ? (
          <Button
            type="submit"
            color="secondary"
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ height: 52 }}
          >
            {t('buttons.newConsult')}
          </Button>
        ) : (
          <Button
            type="submit"
            color="secondary"
            variant="contained"
            sx={{ height: 52 }}
            // disabled={!isValid}
          >
            {t('buttons.consultClient')}
          </Button>
        )
      ) : (
        <Typography variant="h5" color={colors.grey[100]} fontWeight="bold">
          {t('messages.buyMoreTokens')}
        </Typography>
      )}
    </Box>
  );
};

const RedScore = () => {
  const { cookies } = useCookiesLogin();
  const theme = useTheme();
  const [document, setDocument] = useState('');
  const { id: userID, username, bussines_id } = cookies.AUTH_TOKEN;
  const { t } = useTranslation();

  const [dataAPI, setDataAPI] = useState(null);
  const [handleRefresh, setHandleRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState(null);
  const [tokenSerasa, setTokenSerasa] = useState('');
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [pendingOrderData, setPendingOrderData] = useState(null);

  const initialValues = { cpf: '', amount: '' };

  const valuesSchema = yup.object().shape({
    amount: yup.number().required(t('forms.cost')),
    cpf: yup
      .string()
      .required(t('forms.cpfCnpj'))
      .test('cpf-cnpj-valid', t('forms.invalidCpfCnpj'), (value) => isValidCpfCnpj(value)),
  });

  const isValidCpfCnpj = (value) => {
    if (!value) return false;

    const onlyDigits = value.replace(/\D/g, '');

    if (onlyDigits.length === 11) {
      return validateCPF(onlyDigits);
    }

    if (onlyDigits.length === 14) {
      return validateCNPJ(onlyDigits);
    }

    return false;
  };

  const submitConsulta = async (values, bypassModal = false) => {
    setIsLoading(true);
    const cpfCnpj = values.cpf.replace(/\D/g, '');

    if (!cpfCnpj) {
      toast.info(t('messages.cpfCnpjCannotBeEmpty'), { theme: 'dark' });
      setIsLoading(false);
      return;
    }

    const { token } = cookies.AUTH_TOKEN;

    const response = await fetch(`${HOST_API}/v1/order/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        taxvar: cpfCnpj,
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      toast.error(t('messages.consultError'));
      setIsLoading(false);
      return;
    }

    const { data, status } = result || {};
    if (!status) {
      toast.error(t('messages.consultError'));
      setIsLoading(false);
      return;
    }

    const stage = data?.stage;

    if (result.fromCache && (stage === 'PENDING' || stage === 'CREATED')) {
      setPendingOrderData(data);
      setShowPendingModal(true);
      setIsLoading(false);
      return;
    }


    setDataAPI(data);
    setDocument(cpfCnpj);
    setHandleRefresh(true);
    setIsLoading(false);
  };

  const handleWait = () => {
    setShowPendingModal(false);
    setPendingOrderData(null);
  };

  const handleNewConsult = () => {
    setShowPendingModal(false);
    setPendingOrderData(null);
  };

  const handleConfirmReConsulta = async () => {
    setShowModal(false);
    if (pendingFormValues) {
      await submitConsulta(pendingFormValues, true);
    }
  };

  return (
    <Box p={5}>
      <Box mt={10}>
        <ToastContainer />
        <Header
          title={t('sidebar.redScore')}
          subtitle="Informe o CPF ou CNPJ Rural e tenha acesso à pontuação do AGRO SCORE e probabilidade de inadimplência do seu cliente."
        />

        <Formik initialValues={initialValues} validationSchema={valuesSchema}>
          {({ values, errors, touched, handleBlur, handleChange, handleSubmit }) => {
            return (
              <form
                onSubmit={async (event) => {
                  handleSubmit(event);
                  await submitConsulta(values);
                }}
              >
                <Box display="grid" gap="30px" gridTemplateColumns="repeat(4, minmax(0, 1fr))">
                  <TextField
                    fullWidth
                    variant="filled"
                    label={t('forms.cpfCnpj')}
                    type="text"
                    color="secondary"
                    value={formatCNPJ(values.cpf)}
                    onChange={handleChange}
                    inputProps={{ maxLength: 18 }}
                    sx={{ gridColumn: 'span 2' }}
                    name="cpf"
                    error={!!touched.cpf && !!errors.cpf}
                    helperText={touched.cpf && errors.cpf}
                  />
                  <Consulta handleRefresh={handleRefresh} username={username} />
                </Box>
              </form>
            );
          }}
        </Formik>

        {dataAPI?.consult?.stage === 'CONFIRMED' && (
          <>
            {isCPF(dataAPI?.consult?.document) && <HandlerSerasaSocoreCPF dataAPI={dataAPI} />}
            {isCNPJ(dataAPI?.consult?.document) && <HandlerSerasaSocorePj dataAPI={dataAPI} />}
          </>
        )}

        {isLoading && (
          <Backdrop open={isLoading} style={{ zIndex: 9999 }}>
            <CircularProgress color="inherit" sx={{ color: greenColor }} />
          </Backdrop>
        )}

        <ModalPendingConsult
          open={showPendingModal}
          onWait={handleWait}
          onNewConsult={handleNewConsult}
        />

        <Modal open={showModal} onClose={() => setShowModal(false)}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
              width: 400,
            }}
          >
            <Typography variant="h6" gutterBottom>
              {t('consultation.documentAlreadyConsulted')}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {t('consultation.documentAlreadyConsultedMessage')}
            </Typography>
            <Button
              onClick={handleConfirmReConsulta}
              variant="contained"
              color="secondary"
              sx={{ mt: 2 }}
              fullWidth
            >
              {t('buttons.consultAgain')}
            </Button>
          </Box>
        </Modal>
      </Box>
    </Box>
  );
};

export default RedScore;
